import { ChevronLeft, ChevronRight, Quote } from '@digital-twins/design-system/icons'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { glossedQuote } from '@digital-twins/deck-ui/plots/quote-list/glossedQuote'

import { usePersonaThumbFallback } from '@/components/shared/PersonaThumbImg'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import type { QualitativeQuote, QualitativeThemePackage } from '@digital-twins/shared-types'

interface QuoteSpeaker {
  name: string
  blurb?: string
  imageUrl?: string
  segmentLabels?: Array<string>
}

interface VersionOption {
  id: string
  label: string
}

interface OpenEndQuoteGalleryProps {
  themePackage: QualitativeThemePackage
  versions: Array<VersionOption>
  /** Legacy segment shares already carry the exact in-segment quote ids. */
  scopedQuoteIds?: Set<string> | null
  /** Figure-service filters cannot be joined to quote rows in this client. */
  hasActiveFigureFilter: boolean
  speakerFor: (quote: QualitativeQuote) => QuoteSpeaker | null
  initialsFor: (name: string) => string
}

function QuoteCard({
  quote,
  conceptLabel,
  speaker,
  initialsFor,
}: {
  quote: QualitativeQuote
  conceptLabel?: string
  speaker: QuoteSpeaker | null
  initialsFor: (name: string) => string
}) {
  const { displayUrl, handleImageError } = usePersonaThumbFallback(speaker?.imageUrl, 64)

  return (
    <figure className="flex h-full flex-col gap-4 rounded-2xl border border-border bg-transparent p-5">
      {speaker && (
        <figcaption className="flex min-w-0 items-center gap-3">
          {displayUrl ? (
            <img
              src={displayUrl}
              alt={speaker.name}
              onError={() => handleImageError()}
              className="h-10 w-10 shrink-0 rounded-full border border-border/40 object-cover"
            />
          ) : (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-foreground/10 bg-primary/10 text-xs font-medium text-foreground/75">
              {initialsFor(speaker.name)}
            </span>
          )}
          <span className="min-w-0">
            <Text size="sm" className="truncate font-medium text-foreground">{speaker.name}</Text>
            {speaker.blurb && <Text size="xs" color="muted" className="leading-relaxed">{speaker.blurb}</Text>}
            {speaker.segmentLabels?.length ? <span className="mt-2 flex flex-wrap gap-1">{speaker.segmentLabels.map(label => <span key={label} className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-foreground">{label}</span>)}</span> : null}
          </span>
        </figcaption>
      )}
      {!speaker && <Quote aria-hidden="true" className="h-5 w-5 text-muted-foreground" />}
      <blockquote className="text-sm leading-relaxed text-foreground">&ldquo;{glossedQuote(quote.text, quote.textRuns)}&rdquo;</blockquote>
      {conceptLabel && <Text size="xs" color="muted" className="mt-auto">{conceptLabel}</Text>}
    </figure>
  )
}

/** Quote membership and order come from canonical ids, never a text search. */
export function OpenEndQuoteGallery({
  themePackage,
  versions,
  scopedQuoteIds = null,
  hasActiveFigureFilter,
  speakerFor,
  initialsFor,
}: OpenEndQuoteGalleryProps) {
  const { t } = useTranslation('app')
  const [themeId, setThemeId] = useState('all')
  const [assetId, setAssetId] = useState('all')
  const [index, setIndex] = useState(0)
  const viewportRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<Array<HTMLDivElement | null>>([])

  const quotes = useMemo(() => {
    const themes = themeId === 'all'
      ? themePackage.themes
      : themePackage.themes.filter((theme) => theme.id === themeId)
    const memberIds = new Set(themes.flatMap((theme) => theme.quoteIds ?? []))
    const fromPool = (themePackage.quoteCandidates ?? []).filter((quote) => memberIds.has(quote.id))
    const fromLegacySamples = themes
      .filter((theme) => theme.quoteIds == null)
      .flatMap((theme) => theme.quotes)
    const seen = new Set<string>()
    return [...fromPool, ...fromLegacySamples].filter((quote) => {
      if (seen.has(quote.id) || (scopedQuoteIds && !scopedQuoteIds.has(quote.id))) return false
      seen.add(quote.id)
      return assetId === 'all' || quote.assetId === assetId
    })
  }, [themePackage, themeId, assetId, scopedQuoteIds])

  useEffect(() => {
    setThemeId('all')
    setAssetId('all')
    setIndex(0)
  }, [themePackage])

  const selectTheme = (id: string) => { setThemeId(id); setIndex(0); if (viewportRef.current) viewportRef.current.scrollLeft = 0 }
  const selectAsset = (id: string) => { setAssetId(id); setIndex(0); if (viewportRef.current) viewportRef.current.scrollLeft = 0 }
  const goTo = (next: number) => {
    const clamped = Math.max(0, Math.min(quotes.length - 1, next))
    setIndex(clamped)
    const viewport = viewportRef.current
    const first = cardRefs.current[0]
    const target = cardRefs.current[clamped]
    if (viewport && first && target) {
      viewport.scrollLeft = target.offsetLeft - first.offsetLeft
    }
  }
  const syncIndexFromScroll = () => {
    const viewport = viewportRef.current
    const first = cardRefs.current[0]
    if (!viewport || !first || quotes.length < 2) return
    const maxScroll = viewport.scrollWidth - viewport.clientWidth
    if (maxScroll > 0 && viewport.scrollLeft >= maxScroll - 2) {
      setIndex(quotes.length - 1)
      return
    }
    const second = cardRefs.current[1]
    const step = second ? second.offsetLeft - first.offsetLeft : 0
    if (step > 0) setIndex(Math.max(0, Math.min(quotes.length - 1, Math.round(viewport.scrollLeft / step))))
  }

  return (
    <div className="space-y-5" data-testid="question-card-quote-gallery">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('studyReport.questionCard.openEnd.filterTheme')}>
          <Text size="xs" color="muted" className="min-w-14">{t('studyReport.questionCard.openEnd.theme')}</Text>
          {[{ id: 'all', title: t('studyReport.questionCard.openEnd.allThemes') }, ...themePackage.themes.map((theme) => ({ id: theme.id, title: theme.title }))].map((theme) => (
            <button
              key={theme.id}
              type="button"
              aria-pressed={themeId === theme.id}
              data-testid={`question-card-quote-theme-${theme.id}`}
              onClick={() => selectTheme(theme.id)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                themeId === theme.id ? 'border-primary bg-primary/10 font-medium text-foreground' : 'border-border text-muted-foreground hover:text-foreground',
              )}
            >
              {theme.title}
            </button>
          ))}
        </div>
        {versions.length > 1 && (
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('studyReport.questionCard.openEnd.filterConcept')}>
            <Text size="xs" color="muted" className="min-w-14">{t('studyReport.questionCard.openEnd.concept')}</Text>
            {[{ id: 'all', label: t('studyReport.questionCard.openEnd.allConcepts') }, ...versions].map((version) => (
              <button
                key={version.id}
                type="button"
                aria-pressed={assetId === version.id}
                data-testid={`question-card-quote-asset-${version.id}`}
                onClick={() => selectAsset(version.id)}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  assetId === version.id ? 'border-primary bg-primary/10 font-medium text-foreground' : 'border-border text-muted-foreground hover:text-foreground',
                )}
              >
                {version.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {hasActiveFigureFilter ? (
        <Text size="sm" color="muted" data-testid="question-card-quotes-filtered-unavailable">
          {t('studyReport.questionCard.openEnd.filteredUnavailable')}
        </Text>
      ) : quotes.length === 0 ? (
        <Text size="sm" color="muted" data-testid="question-card-quotes-empty">
          {t('studyReport.questionCard.openEnd.empty')}
        </Text>
      ) : (
        <div className="space-y-3" role="group" aria-roledescription="carousel" aria-label={t('studyReport.questionCard.openEnd.quotes')}>
          <div className="flex items-center justify-between gap-3">
            <Text size="xs" color="muted">{t('studyReport.questionCard.openEnd.quoteCount', { count: quotes.length })}</Text>
            <div className="flex items-center gap-2">
              <Text size="xs" color="muted" className="tabular-nums" aria-live="polite">
                {t('studyReport.questionCard.openEnd.position', { current: index + 1, total: quotes.length })}
              </Text>
              <button type="button" aria-label={t('studyReport.questionCard.openEnd.previous')} data-testid="question-card-quote-prev" disabled={index === 0} onClick={() => goTo(index - 1)} className="rounded-full border border-border p-1.5 text-foreground disabled:opacity-40">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button type="button" aria-label={t('studyReport.questionCard.openEnd.next')} data-testid="question-card-quote-next" disabled={index >= quotes.length - 1} onClick={() => goTo(index + 1)} className="rounded-full border border-border p-1.5 text-foreground disabled:opacity-40">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div ref={viewportRef} onScroll={syncIndexFromScroll} className="overflow-x-auto scroll-smooth" data-testid="question-card-quote-viewport">
            <div className="relative flex gap-3 pb-2">
              {quotes.map((quote, position) => (
                <div key={quote.id} ref={(node) => { cardRefs.current[position] = node }} className="w-[85%] shrink-0 sm:w-[calc(50%-0.375rem)]" data-testid={`question-card-quote-item-${quote.id}`}>
                  <QuoteCard quote={quote} conceptLabel={versions.find((version) => version.id === quote.assetId)?.label} speaker={speakerFor(quote)} initialsFor={initialsFor} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
